/**
 * ops2026 — Scheduled jobs board (scheduled-jobs runbook, 2026-09-25).
 *
 * One card per durable background job from `job_run_state`: what it does,
 * how often, when it last ran, the result, and when it is next due. The
 * answer to "is the purge / deletion / billing job actually running?".
 * Sub-hourly timers (escalations, reconciliation, rollups) are not listed;
 * they run continuously and surface in System Health and the logs.
 */
import { RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import type { AdminJobRunState } from '@sill/types/api';

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { Card } from '../../components/atoms/Card.js';
import { Pill } from '../../components/atoms/Pill.js';
import { formatIsoCompact } from '../../lib/format.js';
import { useC } from '../../lib/theme.js';
import {
  JOB_LABELS,
  formatEvery,
  formatSummary,
  jobHealth,
  jobHealthTone,
  jobLabel,
} from './job-labels.js';

const serifFont = "'Instrument Serif', serif";
const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

export function JobsView(): JSX.Element {
  const C = useC();
  const api = useAdminApi();
  const [jobs, setJobs] = useState<readonly AdminJobRunState[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.listJobs();
      setJobs(res.jobs);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load jobs.');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const problems = jobs.filter((j) => {
    const h = jobHealth(j);
    return h === 'failed' || h === 'overdue';
  }).length;

  return (
    <div style={{ padding: 24, maxWidth: 960 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          marginBottom: 18,
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              letterSpacing: 1.5,
              color: C.muted,
              textTransform: 'uppercase',
              marginBottom: 4,
            }}
          >
            Operations · Background jobs
          </div>
          <h1 style={{ fontFamily: serifFont, fontSize: 28, color: C.text, margin: 0 }}>
            Scheduled jobs
          </h1>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontFamily: sansFont,
            fontSize: 12,
            padding: '6px 12px',
            background: 'transparent',
            color: C.muted,
            border: `1px solid ${C.border}`,
            borderRadius: 3,
            cursor: 'pointer',
          }}
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {error !== null ? (
        <Card style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.coralFg }}>{error}</div>
        </Card>
      ) : null}

      {loading ? (
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>Loading…</div>
      ) : null}

      {!loading && error === null ? (
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.body, marginBottom: 14 }}>
          {jobs.length === 0
            ? 'No job has recorded a run yet. Each job runs within a few minutes of a deploy when it is due.'
            : problems === 0
              ? `${jobs.length} jobs recorded. All ran on schedule.`
              : `${jobs.length} jobs recorded. ${problems} need attention.`}
        </div>
      ) : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {jobs.map((j) => {
          const health = jobHealth(j);
          return (
            <Card key={j.job_name} style={{ padding: 16 }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 12,
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ minWidth: 0, flex: '1 1 320px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: sansFont, fontSize: 14, fontWeight: 600, color: C.text }}>
                      {jobLabel(j.job_name)}
                    </span>
                    <Pill tone={jobHealthTone(health)}>{health}</Pill>
                    <span style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>
                      {formatEvery(j.interval_seconds)}
                    </span>
                  </div>
                  {JOB_LABELS[j.job_name] !== undefined ? (
                    <div style={{ fontFamily: sansFont, fontSize: 12.5, color: C.body, marginTop: 4 }}>
                      {JOB_LABELS[j.job_name]?.what}
                    </div>
                  ) : null}
                  <div
                    style={{
                      fontFamily: monoFont,
                      fontSize: 11,
                      color: health === 'failed' ? C.coralFg : C.muted,
                      marginTop: 8,
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {health === 'failed'
                      ? `Last run failed: ${j.last_error_class ?? 'unknown error'}`
                      : `Last result: ${formatSummary(j.last_summary)}`}
                  </div>
                </div>
                <div
                  style={{
                    fontFamily: monoFont,
                    fontSize: 11,
                    color: C.muted,
                    textAlign: 'right',
                    lineHeight: 1.7,
                  }}
                >
                  <div>
                    last{' '}
                    {j.last_completed_at !== null ? formatIsoCompact(j.last_completed_at) : 'never'}
                  </div>
                  <div>next {formatIsoCompact(j.next_due_at)}</div>
                  <div>
                    {j.run_count} runs · {j.failure_count} failed
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
